/**
 * Where a vanity's (or any non-kitchen counter's) splash sits on a wall — the
 * pure geometry the move/resize resync (`fixtureTextureRegionUtils`) and the
 * wall panel's Backsplash card both draw it by. Moved out of
 * `fixtureTextureRegionUtils`, which re-exports what it always exported.
 */
import { wallTopY } from '@shared-lib/kitchen-assembly/styling/backsplashSpec';
import { wallFaceSpanY } from '@shared-lib/geometry/wallFace';
import { isObjectType, isCountertop, isLowerCabinet } from './objectTypeUtils';
import { isSink } from '@shared-lib/sink-types';
import {
  getObjectXZCorners,
  getWallEndpointsXZ,
} from '@shared-lib/kitchen-assembly/styling/backsplashRegion';
import {
  buildShowerFootprint,
  calculateShowerWallTiling,
} from '@shared-lib/geometry/showerWallTiling';
import {
  mirrorBottomOverSplash,
  vanitySplashHeightM,
} from './bathroomBandHeights';

// Tolerance (metres) for edge-alignment detection — matches backsplashHelpers.js.
// An object's edge must be within this distance of a wall AND parallel to it.
const EDGE_ALIGNMENT_TOLERANCE = 0.15;

/**
 * Types whose bounding boxes contribute to backsplash horizontal extent.
 */
export const isBacksplashExtent = (obj) =>
  isCountertop(obj) ||
  isLowerCabinet(obj) ||
  // Both old (stove / oven) and new (range / wall-oven) names accepted.
  isObjectType(obj, 'stove') ||
  isObjectType(obj, 'range') ||
  isObjectType(obj, 'cooktop') ||
  isObjectType(obj, 'oven') ||
  isObjectType(obj, 'wall-oven') ||
  isSink(obj) ||
  isObjectType(obj, 'vanity');

// getObjectXZCorners and getWallEndpointsXZ now live in
// @shared-lib/kitchen-assembly/styling/backsplashRegion so the client "Add
// Backsplash" path and the server-side style_kitchen tool share one geometry
// source (imported at the top of this file).

/**
 * Find walls that have backsplash-extent objects flush against them.
 * Uses edge-alignment detection (same as findAllBacksplashWallIds in
 * backsplashHelpers.js) so that only walls where an object edge is
 * parallel to and flush against the wall are included.
 */
export function findWallsNearObjects(objects, room) {
  const wallIdSet = new Set();
  for (const obj of objects) {
    if (!obj.center || !obj.dimensions) continue;
    const footprint = buildShowerFootprint(obj);
    const tiling = calculateShowerWallTiling(
      footprint,
      room,
      2.4,
      EDGE_ALIGNMENT_TOLERANCE
    );
    for (const side of tiling.sides) {
      if (side.wallId) {
        wallIdSet.add(side.wallId);
      }
    }
  }
  return [...wallIdSet];
}

/**
 * Compute a backsplash region for objects projected onto a wall, using pure
 * geometry (no Three.js). Based on the same projection math as
 * computeTubBacksplashRegion.
 *
 * @param {Object[]} objects - Backsplash-extent objects near this wall
 * @param {string} wallId   - Wall ID
 * @param {Object} room     - Room data
 * @returns {Object|null}    { x, y, width, height } in percentages 0-100
 */
export function computeBacksplashRegionPure(objects, wallId, room) {
  const frame = vanitySplashFrame(objects, wallId, room);
  if (!frame) return null;
  const { wall, wallHeight, wallBottomY, minP, maxP, topY, overSplash } = frame;

  // Backsplash slabs render through percentToWorldCoords() which maps
  // x% linearly from left to right (0% = wall start, 100% = wall end).
  // No UV flip needed here — the flip is only for flat UV-mapped textures
  // (like shower wall tiles) that use the bottomCorners UV convention.
  const regionXPercent = minP;
  const regionWidthPercent = maxP - minP;

  // Vertical: from the top of the countertop, as high as the wall's setting
  // says (`backsplashSpec.vanity`) — 4" with none, as it always was.
  const startPercent = ((topY - wallBottomY) / wallHeight) * 100;
  const heightM = vanitySplashHeightM(wall, room, overSplash, topY);
  const regionHeightPercent = (heightM / wallHeight) * 100;

  return {
    x: Math.max(0, Math.min(100, regionXPercent)),
    y: Math.max(0, Math.min(100, startPercent)),
    width: Math.min(100, regionWidthPercent),
    height: Math.max(0, Math.min(100, regionHeightPercent)),
  };
}

/**
 * The lines a vanity splash's height is measured against on this wall, in
 * world Y: the counter top it stands on, the underside of a mirror hung over
 * it (null with none) and the wall's top — what a dragged top edge snaps to
 * on an elevation (call item #28). The same frame the splash is drawn from.
 */
export function vanitySplashLines(objects, wallId, room) {
  const frame = vanitySplashFrame(objects, wallId, room);
  if (!frame) return null;
  return {
    counterTopY: frame.topY,
    mirrorBottomY: mirrorBottomOverSplash(
      room,
      wallId,
      frame.overSplash,
      frame.topY
    ),
    wallTopY: wallTopY(frame.wall),
  };
}

/**
 * The underside of a mirror hung on this wall over the splash these counter
 * objects would carry, or null — what "To the mirror" runs up to.
 */
export function mirrorOverVanitySplash(objects, wallId, room) {
  const frame = vanitySplashFrame(objects, wallId, room);
  if (!frame) return null;
  return mirrorBottomOverSplash(room, wallId, frame.overSplash, frame.topY);
}

/**
 * Where a counter assembly's splash sits on a wall: its along-wall span in
 * percent (0 = the wall's start), the counter top it stands on, and the test
 * for a mirror over it — a mirror's own projection onto the wall overlapping
 * the span. Null when the objects carry no counter or project to nothing.
 */
function vanitySplashFrame(objects, wallId, room) {
  const wall = room.walls.find((w) => w.id === wallId);
  if (!wall) return null;

  // Percentages of the DRAWN face (`wallFace`), which the 3D tiles over.
  const face = wallFaceSpanY(wall);
  if (!face) return null;
  const wallHeight = face.height;

  // Wall endpoints — bottomCorners[0] = UV 100% (right), [1] = UV 0% (left)
  const endpoints = getWallEndpointsXZ(wall);
  if (!endpoints) return null;

  const wallVec = {
    x: endpoints.end.x - endpoints.start.x,
    z: endpoints.end.z - endpoints.start.z,
  };
  const wallLenSq = wallVec.x * wallVec.x + wallVec.z * wallVec.z;
  if (wallLenSq === 0) return null;

  // Project each object's extent onto the wall direction
  const project = (pt) => {
    const v = { x: pt.x - endpoints.start.x, z: pt.z - endpoints.start.z };
    return ((v.x * wallVec.x + v.z * wallVec.z) / wallLenSq) * 100;
  };

  let minP = 100;
  let maxP = 0;
  let topY = -Infinity;

  for (const obj of objects) {
    if (!isBacksplashExtent(obj) || !obj.center || !obj.dimensions) continue;

    // Project all 4 corners onto wall direction
    const corners = getObjectXZCorners(obj);
    for (const corner of corners) {
      const p = project(corner);
      minP = Math.min(minP, p);
      maxP = Math.max(maxP, p);
    }

    // Track the highest countertop/cabinet surface top
    if (isCountertop(obj) || isLowerCabinet(obj)) {
      const dimY = obj.dimensions.y ?? obj.dimensions.height ?? 0;
      const objTopY = (obj.center.y ?? 0) + dimY / 2;
      if (objTopY > topY) topY = objTopY;
    }
  }

  if (topY === -Infinity || minP >= maxP) return null;

  // Clamp to wall bounds
  minP = Math.max(0, minP);
  maxP = Math.min(100, maxP);

  const overSplash = (mirror) => {
    const ps = getObjectXZCorners(mirror).map(project);
    return Math.min(maxP, Math.max(...ps)) > Math.max(minP, Math.min(...ps));
  };
  return {
    wall,
    wallHeight,
    wallBottomY: face.bottom,
    minP,
    maxP,
    topY,
    overSplash,
  };
}

/**
 * Every backsplash-extent object of the counter assembly `fixtureObject`
 * belongs to — walked up to its root (a sink to its countertop to its
 * cabinet) and back down through the children — which together size its
 * splash on each wall.
 */
export function vanityExtentObjects(fixtureObject, room) {
  // If the moved object is a child (e.g. sink), walk UP to find the root
  // parent (countertop/cabinet) so we collect the full fixture group.
  let rootObject = fixtureObject;
  const walkedUp = new Set();
  while (rootObject?.parents?.length > 0 && !walkedUp.has(rootObject.id)) {
    walkedUp.add(rootObject.id);
    const parentId = rootObject.parents[0]?.id;
    const parent = room.objects?.find((o) => o.id === parentId);
    if (parent && isBacksplashExtent(parent)) {
      rootObject = parent;
    } else {
      break;
    }
  }

  // Collect all backsplash-extent objects related to this fixture.
  // Walk down from the root to find countertops, cabinets, sinks, etc.
  const extentObjects = [];
  const visited = new Set();

  const collectExtent = (objId) => {
    if (!objId || visited.has(objId)) return;
    visited.add(objId);
    const obj = room.objects?.find((o) => o.id === objId);
    if (!obj) return;
    if (isBacksplashExtent(obj)) {
      extentObjects.push(obj);
    }
    // Find children that list this object as a parent
    for (const child of room.objects) {
      if (child.parents?.some((p) => p.id === objId)) {
        collectExtent(child.id);
      }
    }
  };
  collectExtent(rootObject.id);
  return extentObjects;
}
